import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Control, useForm } from "react-hook-form";
import { FiChevronDown, FiMail, FiTrash2 } from "react-icons/fi";
import { FaSlack } from "react-icons/fa";
import { LuLayers } from "react-icons/lu";
import { generatePath } from "react-router";
import CDS from "design-system";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { companyKeys, useCurrentCompanyAdminQuery } from "features/Company/hooks";
import UserSelectField from "components/FormElements/Fields/SelectFields/UserSelectField";
import { Company, TaskNotificationChannel } from "models/company";
import apiClient from "services/ApiClient";
import {
  useCreateOverride,
  useDeleteOverride,
  useOverridesQuery,
  useUpdateOverride,
} from "features/TaskNotificationChannelOverrides/hooks";

const CHANNEL_OPTIONS: Array<{
  value: TaskNotificationChannel;
  label: string;
  icon: typeof LuLayers;
}> = [
  { value: "both", label: "Email and Slack", icon: LuLayers },
  { value: "message", label: "Slack only", icon: FaSlack },
  { value: "email", label: "Email only", icon: FiMail },
];

function channelLabel(channel: TaskNotificationChannel): string {
  return CHANNEL_OPTIONS.find((o) => o.value === channel)?.label ?? channel;
}

function ChannelMenu({
  value,
  onChange,
  isDisabled,
  width = "160px",
}: {
  value: TaskNotificationChannel;
  onChange: (next: TaskNotificationChannel) => void;
  isDisabled?: boolean;
  width?: string;
}) {
  const selected = CHANNEL_OPTIONS.find((o) => o.value === value) ?? CHANNEL_OPTIONS[0];
  return (
    <Menu matchWidth>
      <MenuButton
        as={Button}
        variant="outline"
        size="xs"
        colorScheme="gray"
        rightIcon={<FiChevronDown color="var(--chakra-colors-gray-500)" />}
        isDisabled={isDisabled}
        minW={width}
        maxW={width}
        textAlign="left"
        fontWeight="normal"
        color="content.primary"
        px={2}
        h="28px"
      >
        <HStack spacing={1.5}>
          <Box as={selected.icon} fontSize="xs" color="content.secondary" />
          <CDS.Text12 color="content.primary">{selected.label}</CDS.Text12>
        </HStack>
      </MenuButton>
      <MenuList shadow="md">
        {CHANNEL_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} icon={<Box as={opt.icon} />} onClick={() => onChange(opt.value)}>
            <CDS.Text14 fontWeight={value === opt.value ? "semibold" : "normal"}>
              {opt.label}
            </CDS.Text14>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

function CompanyDefaultPicker() {
  const { data: company, isLoading } = useCurrentCompanyAdminQuery();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<TaskNotificationChannel | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (channel: TaskNotificationChannel) =>
      apiClient.put<Company>(generatePath(API_ROUTES.companies.show, { id: "current" }), {
        company: { task_notification_channel: channel },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.current() });
      setPending(null);
      toast({ title: "Default updated", status: "success" });
    },
    onError: () => {
      setPending(null);
      toast({ title: "Could not save", status: "error" });
    },
  });

  if (isLoading || !company) {
    return <Skeleton h="50px" />;
  }

  const persisted: TaskNotificationChannel = company.taskNotificationChannel ?? "both";

  return (
    <>
      <HStack align="center" spacing={4}>
        <Box flex={1}>
          <CDS.Text14 fontWeight="medium">Default channel</CDS.Text14>
          <CDS.Text12 color="content.secondary">
            What everyone gets unless listed below.
          </CDS.Text12>
        </Box>
        <ChannelMenu
          value={persisted}
          onChange={(next) => { if (next !== persisted) setPending(next); }}
          isDisabled={isPending}
        />
      </HStack>

      <AlertDialog
        isOpen={pending !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => setPending(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md" fontWeight="semibold">
              Switch the default to {pending ? channelLabel(pending) : ""}?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Everyone without a user-specific setting below will start receiving task notifications via{" "}
              <Text as="span" fontWeight="semibold">{pending ? channelLabel(pending) : ""}</Text>.
              This affects new task assignments, reminders, due-date pings, and escalations.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setPending(null)} isDisabled={isPending}>
                Cancel
              </Button>
              <Button
                colorScheme="cdsIndigo"
                ml={3}
                onClick={() => pending && mutate(pending)}
                isLoading={isPending}
              >
                Switch default
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

interface AddOverrideFormValues {
  userId: string;
}

function AddOverrideModal({ isOpen, onClose, excludeUserIds }: { isOpen: boolean; onClose: () => void; excludeUserIds: string[] }) {
  const toast = useToast();
  const { control, handleSubmit, reset } = useForm<AddOverrideFormValues>({
    defaultValues: { userId: "" },
  });
  const [channel, setChannel] = useState<TaskNotificationChannel>("email");
  const { mutate, isPending } = useCreateOverride();

  function handleClose() {
    reset();
    setChannel("email");
    onClose();
  }

  function onSubmit(values: AddOverrideFormValues) {
    if (!values.userId) return;
    mutate(
      { userId: values.userId, channel },
      {
        onSuccess: () => {
          toast({ title: "Added", status: "success" });
          handleClose();
        },
        onError: () => {
          toast({ title: "Could not add", status: "error" });
        },
      },
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader fontSize="md">Add user</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <UserSelectField
                control={control as unknown as Control}
                name="userId"
                label="User"
                required
                excludedIds={excludeUserIds}
              />
              <Box>
                <CDS.Text14 mb={1.5}>Channel</CDS.Text14>
                <ChannelMenu value={channel} onChange={setChannel} width="200px" />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="ghost" onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                colorScheme="cdsIndigo"
                isLoading={isPending}
              >
                Add user
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

function ExceptionsList() {
  const { data: overrides = [], isLoading } = useOverridesQuery();
  const { mutate: updateOverride } = useUpdateOverride();
  const { mutate: deleteOverride } = useDeleteOverride();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <VStack align="stretch" spacing={2} mt={6}>
      <Box>
        <CDS.Text14 fontWeight="medium">User-specific task notifications</CDS.Text14>
        <CDS.Text12 color="content.secondary">
          These users get a different channel than the default above.
        </CDS.Text12>
      </Box>

      {isLoading ? (
        <Skeleton h="60px" />
      ) : overrides.length > 0 ? (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Channel</Th>
              <Th width="40px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {overrides.map((ovr) => (
              <Tr key={ovr.id}>
                <Td>
                  <CDS.Text14>{ovr.user?.fullName ?? `User ${ovr.userId}`}</CDS.Text14>
                  {ovr.user?.workEmail && (
                    <CDS.Text12 color="content.tertiary">{ovr.user.workEmail}</CDS.Text12>
                  )}
                </Td>
                <Td>
                  <ChannelMenu
                    value={ovr.channel}
                    onChange={(next) =>
                      updateOverride(
                        { id: ovr.id, payload: { channel: next } },
                        {
                          onSuccess: () => toast({ title: "Updated", status: "success" }),
                          onError: () => toast({ title: "Could not update", status: "error" }),
                        },
                      )
                    }
                  />
                </Td>
                <Td>
                  <IconButton
                    aria-label={`Remove user-specific channel for ${ovr.user?.fullName ?? "user"}`}
                    icon={<FiTrash2 />}
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      deleteOverride(ovr.id, {
                        onSuccess: () => toast({ title: "Removed", status: "success" }),
                        onError: () => toast({ title: "Could not remove", status: "error" }),
                      })
                    }
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <CDS.Text12 color="content.tertiary" fontStyle="italic" py={1}>
          No one yet.
        </CDS.Text12>
      )}

      <Flex pt={2}>
        <Button size="sm" variant="ghost" colorScheme="cdsIndigo" onClick={onOpen}>
          + Add user
        </Button>
      </Flex>

      <AddOverrideModal
        isOpen={isOpen}
        onClose={onClose}
        excludeUserIds={overrides.map((o) => String(o.userId))}
      />
    </VStack>
  );
}

// channelLabel is internal to the AlertDialog body — kept here, not exported.
export default function TaskNotificationChannelSettings() {
  return (
    <Card minWidth="900px" width="calc(100% - 200px)" mt="4">
      <CardBody>
        <Flex justifyContent="space-between" w="100%" alignItems="center" p={5}>
          <Box opacity="70%" fontSize="2xl">
            Task notification channels
          </Box>
        </Flex>
        <VStack align="stretch" spacing={4} px={10} pb={6}>
          <CDS.Text14 color="content.secondary">
            How automated task notifications (new assignments, reminders, due-date pings, escalations) reach your team.
          </CDS.Text14>
          <CompanyDefaultPicker />
          <ExceptionsList />
        </VStack>
      </CardBody>
    </Card>
  );
}

